// LOOTUS — Editor expandido da "Descrição / Histórico do serviço" (Ficha de
// Serviço). Configuração compartilhada do editor Tiptap + utilitários de
// compatibilidade com fichas antigas (texto puro) + sanitização de
// exibição (defesa em profundidade — o limite de segurança real é o
// backend, ver backend/security/rich_text.py; aqui é só a segunda camada,
// nunca a única, item 8 do pedido).
import DOMPurify from "dompurify";
import StarterKit from "@tiptap/starter-kit";
import TextAlign from "@tiptap/extension-text-align";
import Placeholder from "@tiptap/extension-placeholder";
// FontFamily/FontSize já vêm prontos, oficiais e estáveis dentro do
// próprio @tiptap/extension-text-style (o pacote separado
// @tiptap/extension-font-size só existe como prerelease "-next" hoje —
// evitado por instabilidade; a extensão real já está aqui, versão 3.30.6
// estável).
import { TextStyle, FontFamily, FontSize } from "@tiptap/extension-text-style";

// Mesmo allowlist do backend (backend/security/rich_text.py) — mantidos em
// espelho manual (nenhum dos dois lados importa o outro) porque um é
// Python e o outro é JS; qualquer mudança de tag/atributo/estilo precisa
// ser replicada nos dois arquivos.
export const RICH_TEXT_ALLOWED_TAGS = ["p", "br", "strong", "em", "u", "h2", "h3", "ul", "ol", "li", "blockquote", "hr", "span"];
export const RICH_TEXT_ALLOWED_ATTR = ["style"];

export const FONT_FAMILY_OPTIONS = [
  { value: "", label: "Padrão do LOOTUS" },
  { value: "Arial, sans-serif", label: "Arial" },
  { value: "Verdana, sans-serif", label: "Verdana" },
  { value: "Georgia, serif", label: "Georgia" },
  { value: "'Times New Roman', serif", label: "Times New Roman" },
];

// "Estilo" único (item 4 + item 5 do pedido combinados numa única lista
// para não duplicar controles na toolbar) — tamanho pequeno/grande via
// marca de fonte (FontSize) e Título/Subtítulo via nível de heading.
export const TEXT_STYLE_OPTIONS = [
  { value: "normal", label: "Texto normal" },
  { value: "pequeno", label: "Pequeno" },
  { value: "grande", label: "Grande" },
  { value: "titulo", label: "Título" },
  { value: "subtitulo", label: "Subtítulo" },
];

// StarterKit (Tiptap 3) já inclui Underline/Strike/Link por padrão — Strike
// e Link não fazem parte da toolbar pedida (nem do allowlist do backend),
// então são desligados aqui em vez de deixar o editor produzir marcação
// que o backend descartaria silenciosamente ao salvar.
export function buildRichTextExtensions(placeholder) {
  return [
    StarterKit.configure({
      link: false,
      strike: false,
      heading: { levels: [2, 3] },
    }),
    TextStyle,
    FontFamily,
    FontSize,
    TextAlign.configure({ types: ["paragraph", "heading"] }),
    Placeholder.configure({ placeholder: placeholder || "" }),
  ];
}

// Heurística de compatibilidade — ver item 9 do pedido. Todo conteúdo
// gerado pelo editor (editor.getHTML()) sempre começa com uma dessas tags
// de bloco; uma ficha antiga (texto puro, eventualmente com um "<" solto
// no meio do texto) nunca começa assim. Evita falso positivo em texto
// livre antigo que por acaso contenha um "<".
const RICH_HTML_START_RE = /^\s*<(p|h2|h3|ul|ol|blockquote|hr)\b/i;
export function isLikelyRichHtml(value) {
  return typeof value === "string" && RICH_HTML_START_RE.test(value);
}

function escapeHtml(str) {
  return str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

// Ficha antiga (texto puro, sem nenhuma tag) → HTML válido para o editor
// abrir sem perder as quebras de linha (mesma leitura visual de antes,
// via whitespace-pre-wrap). Nunca reinterpreta o texto como marcação.
export function legacyPlainTextToHtml(value) {
  const text = value || "";
  if (!text) return "<p></p>";
  return `<p>${escapeHtml(text).replace(/\n/g, "<br>")}</p>`;
}

// Conteúdo inicial do editor — usado só na criação do editor (mount), ver
// componentes em RichTextEditor.jsx.
export function normalizeEditorContent(value) {
  if (!value) return "<p></p>";
  return isLikelyRichHtml(value) ? value : legacyPlainTextToHtml(value);
}

// Sanitização de EXIBIÇÃO (defesa em profundidade, nunca o único filtro —
// o backend já sanitizou antes de gravar). Usada por RichTextView.
export function sanitizeRichTextForDisplay(html) {
  return DOMPurify.sanitize(html || "", {
    ALLOWED_TAGS: RICH_TEXT_ALLOWED_TAGS,
    ALLOWED_ATTR: RICH_TEXT_ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
  });
}

// O dropdown único "Estilo" (item 6 do pedido — toolbar limpa, um controle
// só em vez de dois) combina nível de título (heading) com tamanho de
// fonte (marca própria FontSize) — nunca são independentes na UI.
export const FONT_SIZE_PEQUENO = "12px";
export const FONT_SIZE_GRANDE = "20px";

export function applyTextStyle(editor, value) {
  if (!editor) return;
  const chain = editor.chain().focus();
  if (value === "titulo") { chain.unsetFontSize().setNode("paragraph").setHeading({ level: 2 }).run(); return; }
  if (value === "subtitulo") { chain.unsetFontSize().setNode("paragraph").setHeading({ level: 3 }).run(); return; }
  // normal/pequeno/grande sempre voltam para parágrafo (nunca ficam "presos" num heading)
  chain.setParagraph();
  if (value === "pequeno") chain.setFontSize(FONT_SIZE_PEQUENO);
  else if (value === "grande") chain.setFontSize(FONT_SIZE_GRANDE);
  else chain.unsetFontSize();
  chain.run();
}

export function getActiveTextStyle(editor) {
  if (!editor) return "normal";
  if (editor.isActive("heading", { level: 2 })) return "titulo";
  if (editor.isActive("heading", { level: 3 })) return "subtitulo";
  const attrs = editor.getAttributes("textStyle");
  if (attrs.fontSize === FONT_SIZE_PEQUENO) return "pequeno";
  if (attrs.fontSize === FONT_SIZE_GRANDE) return "grande";
  return "normal";
}
